// FILE: src/components/admin/AccommodationRequestCard.jsx
// One accommodation request (MTO 12.3; AODA) as staff see it on the list
// and on its own page: who sent it, the reference, how they prefer to be
// contacted, what they need, the notes, and the status / note form
// (PATCH /api/admin/accommodations/:id { status?, note? } → the request;
// every change is audited on the server).
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { StatusChip, visuallyHidden } from "../ui";
import { errorMessage } from "../../i18n/errors";
import { formatDate, formatDateTime } from "./format";

export const ACCOMMODATION_STATUS = {
  NEW: { label: "New", tone: "warning", Icon: MarkEmailUnreadOutlinedIcon },
  IN_PROGRESS: { label: "In progress", tone: "info", Icon: AutorenewIcon },
  CLOSED: { label: "Closed", tone: "success", Icon: CheckCircleOutlineIcon }
};

export function AccommodationStatusChip({ status }) {
  const s = ACCOMMODATION_STATUS[status] || ACCOMMODATION_STATUS.NEW;
  const Icon = s.Icon;
  return <StatusChip label={s.label} tone={s.tone} icon={<Icon />} />;
}

export function preferredContactText(value) {
  return String(value || "").toUpperCase() === "PHONE" ? "a phone call" : "email";
}

/**
 * `detailed`: the request's own page (full dates, the h2 is the needs
 * heading); otherwise a list card with a link to the request's page.
 */
export default function AccommodationRequestCard({ item, onSaved, detailed = false }) {
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const headingId = `acc-${item._id}`;

  useEffect(() => {
    setStatus(item.status);
  }, [item.status]);

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await fetchWithAuth(`/api/admin/accommodations/${item._id}`, {
        method: "PATCH",
        body: JSON.stringify({
          ...(status !== item.status ? { status } : {}),
          ...(note.trim() ? { note: note.trim() } : {})
        })
      });
      setNote("");
      setSaved(true);
      onSaved?.(updated);
    } catch (err) {
      setError(err || "INTERNAL_ERROR");
    } finally {
      setBusy(false);
    }
  }

  const notes = Array.isArray(item.notes) ? item.notes : [];
  const unchanged = status === item.status && !note.trim();

  return (
    <Card component="article" aria-labelledby={headingId}>
      <CardContent>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography id={headingId} variant="h3" component="h2" sx={{ overflowWrap: "anywhere" }}>
              {detailed ? "The request" : item.name}
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              {item.reference ? `Reference ${item.reference} · ` : ""}Received {formatDateTime(item.createdAt)}
            </Typography>
          </Box>
          <Box>
            <AccommodationStatusChip status={item.status} />
          </Box>
        </Stack>

        <Stack spacing={0.5} sx={{ mb: 1.5 }}>
          {detailed && <Typography sx={{ overflowWrap: "anywhere" }}>Name: {item.name}</Typography>}
          <Typography sx={{ overflowWrap: "anywhere" }}>
            Email: <Link href={`mailto:${item.email}`}>{item.email}</Link>
          </Typography>
          {item.phone && (
            <Typography>
              Phone: <Link href={`tel:${String(item.phone).replace(/[^\d+]/g, "")}`}>{item.phone}</Link>
            </Typography>
          )}
          <Typography>Prefers: {preferredContactText(item.preferredContact)}</Typography>
        </Stack>

        <Typography component="h3" sx={{ fontWeight: 650, mb: 0.5 }}>
          What they need
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: "background.default",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            mb: 2,
            ...(detailed ? {} : { display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden" })
          }}
        >
          {item.needs}
        </Box>

        {notes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography component="h3" sx={{ fontWeight: 650, mb: 0.5 }}>
              Notes
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {notes.map((n, i) => (
                <Typography component="li" key={`${n.at}-${i}`} sx={{ mb: 0.5, overflowWrap: "anywhere" }}>
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {detailed ? formatDateTime(n.at) : formatDate(n.at)}:
                  </Box>{" "}
                  {n.text}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {errorMessage(error)}
          </Alert>
        )}
        {saved && detailed && (
          <Alert severity="success" role="status" sx={{ mb: 2 }} onClose={() => setSaved(false)}>
            Saved.
          </Alert>
        )}

        <Box component="form" noValidate onSubmit={save} aria-label={`Update the request from ${item.name}`}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ alignItems: { md: "flex-start" } }}>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel id={`acc-status-${item._id}`}>Status</InputLabel>
              <Select
                labelId={`acc-status-${item._id}`}
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setSaved(false);
                }}
              >
                {Object.entries(ACCOMMODATION_STATUS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              id={`acc-note-${item._id}`}
              label="Add a note (optional)"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setSaved(false);
              }}
              multiline={detailed}
              minRows={detailed ? 2 : undefined}
              slotProps={{ htmlInput: { maxLength: 5000 } }}
              sx={{ flex: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={busy || unchanged}
              sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </Box>

        {!detailed && (
          <Box sx={{ mt: 2 }}>
            <Link component={RouterLink} to={`/admin/accommodations/${item._id}`}>
              View request
              <Box component="span" sx={visuallyHidden}>
                {` from ${item.name}`}
              </Box>
            </Link>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
